import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, Sheet, Stack, Typography } from '@mui/joy';
import { motion, useReducedMotion } from 'framer-motion';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import ChatBubble from './ChatBubble';
import MessagesPaneHeader from './MessagesPaneHeader';
import MessageInput from './MessageInput';
import { LayoutContext } from '../../context';
import { APIAddChatUser, APIGetChatMessagesById, APISendMessage, createWebSocketConnection } from '../../api';
import EmptyState from '../ui/EmptyState';
import './thread.css';

const sameId = (left, right) => left != null && right != null && String(left) === String(right);
const mergeMessages = (oldMessages, incoming) => {
  const merged = new Map(oldMessages.map(message => [String(message.id), message]));
  incoming.filter(message => message?.id != null).forEach(message => {
    const key = String(message.id);
    merged.set(key, { ...merged.get(key), ...message });
  });
  return [...merged.values()].sort((a, b) => {
    const difference = new Date(a.date_create).getTime() - new Date(b.date_create).getTime();
    return difference || Number(a.id) - Number(b.id);
  });
};
const dayLabel = date => {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  return value.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
};

/** One full-width conversation. HTTP results and socket echoes share message ids. */
export default function MessagesPane({ chat, setSelectedChat, searchParams, setSearchParams, setChats, onBack }) {
  const { token, currentUser } = useContext(LayoutContext);
  const reduceMotion = useReducedMotion();
  const chatId = chat?.id ?? chat?.chat_id;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [sendError, setSendError] = useState('');
  const [textAreaValue, setTextAreaValue] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [joined, setJoined] = useState(false);
  const [joiningCommunity, setJoiningCommunity] = useState(false);
  const [wsConnectionStatus, setWsConnectionStatus] = useState(false);
  const messagesContainerRef = useRef(null);
  const messagesRef = useRef([]);
  const generationRef = useRef(0);
  const loadingRef = useRef(false);
  const sendingRef = useRef(false);
  const scrollRef = useRef({ type: 'bottom' });
  const liveChatRef = useRef(chatId);
  liveChatRef.current = chatId;
  const isMember = !chat?.is_community || joined || Boolean(chat.members?.some(member =>
    sameId(member.profile_data?.user_id, currentUser?.id)
  ));

  useEffect(() => {
    setTextAreaValue('');
    setAttachments([]);
    setSendError('');
    setJoined(false);
    setSending(false);
    sendingRef.current = false;
  }, [chatId]);

  const updatePreview = useCallback(lastMessage => {
    if (typeof setChats !== 'function') return;
    setChats(items => (items || []).map(item => sameId(item.id ?? item.chat_id, chatId)
      ? { ...item, last_message: lastMessage || null, last_msg: lastMessage
        ? { text: lastMessage.content || (lastMessage.attachments?.length ? 'Вложение' : ''), datetime: lastMessage.date_create }
        : null }
      : item));
  }, [chatId, setChats]);

  const loadMessages = useCallback(async (initial = false) => {
    if (chatId == null || loadingRef.current) return;
    loadingRef.current = true;
    const generation = generationRef.current;
    setLoading(true);
    setLoadError('');
    const container = messagesContainerRef.current;
    const anchor = container ? { type: 'keep', top: container.scrollTop, height: container.scrollHeight } : null;
    try {
      const response = await APIGetChatMessagesById(chatId, token, 30, initial ? 0 : messagesRef.current.length);
      if (generationRef.current !== generation) return;
      const incoming = [...(response?.messages || [])].reverse();
      scrollRef.current = initial ? { type: 'bottom' } : anchor;
      setMessages(previous => mergeMessages(previous, incoming));
      setHasMore(Boolean(response?.has_more));
    } catch (_) {
      if (generationRef.current === generation) setLoadError('Не удалось загрузить сообщения. Попробуйте ещё раз.');
    } finally {
      if (generationRef.current === generation) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [chatId, token]);

  useEffect(() => {
    generationRef.current += 1;
    messagesRef.current = [];
    loadingRef.current = false;
    setMessages([]);
    setLoadError('');
    setHasMore(false);
    if (chatId != null && isMember) loadMessages(true);
    return () => { generationRef.current += 1; };
  }, [chatId, isMember, loadMessages]);

  useLayoutEffect(() => {
    messagesRef.current = messages;
    const container = messagesContainerRef.current;
    const scroll = scrollRef.current;
    if (container && scroll?.type === 'bottom') container.scrollTop = container.scrollHeight;
    if (container && scroll?.type === 'keep') container.scrollTop = scroll.top + container.scrollHeight - scroll.height;
    scrollRef.current = null;
  }, [messages]);

  useEffect(() => {
    setWsConnectionStatus(false);
    if (chatId == null || !isMember) return undefined;
    let active = true;
    const connection = createWebSocketConnection(chatId, token, data => {
      if (!active) return;
      const incoming = data?.data?.message;
      const action = data?.data?.action;
      if (incoming?.id == null || (incoming.chat_id != null && !sameId(incoming.chat_id, chatId))) return;
      if (action && !['created', 'updated', 'deleted'].includes(action)) return;
      const container = messagesContainerRef.current;
      const nearBottom = !container || container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      if (nearBottom || sameId(incoming.sender?.id, currentUser?.id)) scrollRef.current = { type: 'bottom' };
      const next = action === 'deleted'
        ? messagesRef.current.filter(message => !sameId(message.id, incoming.id))
        : mergeMessages(messagesRef.current, [incoming]);
      messagesRef.current = next;
      setMessages(next);
      updatePreview(next[next.length - 1]);
    }, connected => { if (active) setWsConnectionStatus(connected); });
    return () => {
      active = false;
      connection.close?.();
    };
  }, [chatId, token, isMember, currentUser?.id, updatePreview]);

  const handleSendMessage = async () => {
    if (sendingRef.current || !isMember || (!textAreaValue.trim() && !attachments.length)) return;
    // The current API requires text even when attachments are present.
    if (!textAreaValue.trim()) { setSendError('Добавьте короткую подпись к файлу.'); return; }
    sendingRef.current = true;
    setSending(true);
    setSendError('');
    const sendingChat = chatId;
    const content = textAreaValue;
    const files = [...attachments];
    try {
      const response = await APISendMessage(chatId, { content }, token, files);
      if (!sameId(liveChatRef.current, sendingChat)) return;
      const sent = response?.message || response;
      if (sent?.id != null) {
        scrollRef.current = { type: 'bottom' };
        const next = mergeMessages(messagesRef.current, [sent]);
        messagesRef.current = next;
        setMessages(next);
        updatePreview(next[next.length - 1]);
      }
      setTextAreaValue('');
      setAttachments([]);
    } catch (_) {
      if (sameId(liveChatRef.current, sendingChat)) setSendError('Сообщение не отправлено. Текст и файлы сохранены — попробуйте ещё раз.');
    } finally {
      if (sameId(liveChatRef.current, sendingChat)) {
        sendingRef.current = false;
        setSending(false);
      }
    }
  };

  const handleJoinCommunity = async () => {
    if (chatId == null || joiningCommunity) return;
    const joiningChat = chatId;
    setJoiningCommunity(true);
    setLoadError('');
    try {
      await APIAddChatUser(token, chatId, { profile_id: currentUser.profile_id });
      if (sameId(liveChatRef.current, joiningChat)) setJoined(true);
    } catch (_) {
      if (sameId(liveChatRef.current, joiningChat)) setLoadError('Не удалось присоединиться к сообществу. Попробуйте ещё раз.');
    } finally {
      if (sameId(liveChatRef.current, joiningChat)) setJoiningCommunity(false);
    }
  };

  if (!chat) return <Box sx={{ display: 'grid', placeItems: 'center', height: '100%', p: 3 }}>
    <EmptyState icon={<ForumRoundedIcon />} title="Ваши разговоры здесь" text="Выберите диалог, чтобы продолжить знакомство." />
  </Box>;

  return (
    <Sheet className="lv-thread" sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <MessagesPaneHeader chat={chat} setSelectedChat={setSelectedChat} searchParams={searchParams}
        setSearchParams={setSearchParams} wsConnected={wsConnectionStatus} onBack={onBack} />
      {!isMember ? (
        <Box className="lv-thread-join">
          <Box className="lv-thread-join__icon"><GroupsRoundedIcon sx={{ fontSize: 36, color: 'inherit' }} /></Box>
          <Typography level="h3">{chat.name || 'Сообщество'}</Typography>
          <Typography level="body-sm" sx={{ color: 'var(--lvc-ink-soft)', maxWidth: 420, lineHeight: 1.7 }}>
            {chat.description || 'Присоединитесь, чтобы читать сообщения и участвовать в разговоре.'}
          </Typography>
          <Typography level="body-xs">{chat.member_count || 0} участников{chat.city ? ` · ${chat.city}` : ''}</Typography>
          {loadError && <Alert color="danger" size="sm">{loadError}</Alert>}
          <Button className="lvc-grad-btn" loading={joiningCommunity} onClick={handleJoinCommunity} sx={{ borderRadius: 14 }}>Присоединиться</Button>
        </Box>
      ) : (
        <>
          <Box ref={messagesContainerRef} className="lv-thread-messages lvc-scroll" role="log" aria-label="Сообщения чата" aria-live="polite">
            {hasMore && <Box sx={{ textAlign: 'center', mb: 2 }}><Button size="sm" variant="soft" onClick={() => loadMessages(false)} loading={loading}
              startDecorator={<KeyboardArrowUpRoundedIcon sx={{ color: 'inherit' }} />} sx={{ borderRadius: 14 }}>Предыдущие сообщения</Button></Box>}
            {loading && !messages.length && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size="sm" aria-label="Загрузка сообщений" /></Box>}
            {loadError && <Alert color="danger" sx={{ mb: 2 }} endDecorator={<Button size="sm" color="danger" onClick={() => loadMessages(!messages.length)}>Повторить</Button>}>{loadError}</Alert>}
            {!loading && !messages.length && !loadError && <Box className="lv-thread-first">
              <ForumRoundedIcon sx={{ fontSize: 32, color: '#a586c6', mb: 1 }} />
              <Typography level="title-md">Знакомство начинается с «привет»</Typography>
              <Typography level="body-sm" sx={{ color: 'var(--lvc-ink-soft)', mt: 0.75 }}>Напишите первым — здесь может начаться что-то хорошее.</Typography>
            </Box>}
            {messages.map((message, index) => {
              const mine = sameId(message.sender?.id, currentUser?.id);
              const system = message.type_sender === 'system';
              const date = dayLabel(message.date_create);
              const showDay = date && date !== dayLabel(messages[index - 1]?.date_create);
              return <Box key={message.id}>
                {showDay && <Box className="lv-thread-day"><span>{date}</span></Box>}
                <Box component={motion.div} initial={reduceMotion ? false : { opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}
                  className={`lv-thread-row${mine ? ' is-mine' : ''}${system ? ' is-system' : ''}`}>
                  {!mine && !system && chat.is_community && <Avatar size="sm" src={message.sender?.photo_file_path} sx={{ flex: 'none', '--Avatar-size': '28px' }}>{(message.sender?.full_name || '?')[0]}</Avatar>}
                  <ChatBubble message={message} isYou={mine} showName={Boolean(chat.is_community)} />
                </Box>
              </Box>;
            })}
          </Box>
          <MessageInput textAreaValue={textAreaValue} setTextAreaValue={setTextAreaValue} onSubmit={handleSendMessage}
            attachments={attachments} sending={sending} error={sendError}
            onAttach={event => {
              const files = Array.from(event.target.files || []);
              setAttachments(previous => [...previous, ...files]);
              setSendError('');
              event.target.value = '';
            }} onRemoveAttachment={index => setAttachments(previous => previous.filter((_, i) => i !== index))} />
        </>
      )}
    </Sheet>
  );
}
